import { VarianteChip } from '../ui';
import { BikeEletrica, BikeMarca } from '../api/bike-eletrica/bikeEletricaApi';

/**
 * Regras de apresentação do catálogo de motocicletas elétricas (at-69, D7).
 *
 * Os quatro rótulos abaixo eram `title` na tabela antiga — invisíveis no celular
 * e no teclado (at-65), que é onde o TI usa a tela. Saem da view pela convenção
 * da at-24: a lista e a ficha mostram a mesma informação e não podem divergir.
 *
 * 📛 **"Motocicleta elétrica" é o nome do produto na interface desde a at-54**
 * (item 57). Os identificadores seguem com "bike" de propósito — renomeá-los
 * custaria migration e refactor sem o usuário ver diferença, e o prefixo de placa
 * `BIK` já está gravado no SGA.
 */

export interface RotuloChip {
  texto: string;
  variante: VarianteChip;
  descricao?: string;
}

export function rotuloSituacaoBike(ativo: boolean): RotuloChip {
  return ativo
    ? { texto: 'Ativa', variante: 'ok' }
    : {
        texto: 'Inativa',
        variante: 'neutro',
        descricao: 'Não aparece para seleção na adesão. O catálogo nunca apaga.',
      };
}

/**
 * `SEED` = veio da carga inicial e **ninguém revisou o valor** — que é referência
 * aproximada, já que motocicleta elétrica não tem tabela oficial. A primeira
 * edição promove a linha para `MANUAL` (regra do backend, at-45).
 *
 * ⚠️ É **pendente**, não erro: o valor está lá e funciona; o que falta é alguém
 * conferir. `null` quando a linha já foi revisada.
 */
export function rotuloOrigemBike(origem: BikeEletrica['origem']): RotuloChip | null {
  if (origem !== 'SEED') return null;

  return {
    texto: 'Carga inicial',
    variante: 'pendente',
    descricao:
      'Valor da carga inicial — referência aproximada, ainda não revisada pelo TI. Editar este modelo o marca como revisado.',
  };
}

/**
 * O código FIPE **fictício** do modelo no SGA (at-46).
 *
 * 🔴 Sem ele a adesão **fecha no SAAC mas não sobe ao SGA**, e o veículo fica
 * fora da fila do cron — é a única pendência do sistema que o cron não cobre. Por
 * isso o estado vazio é chip de pendência com o texto dizendo a consequência, e
 * não um traço.
 */
export function rotuloCodigoSga(codigo: string | null): RotuloChip {
  if (codigo) {
    return {
      texto: codigo,
      variante: 'ok',
      descricao: 'Modelo cadastrado no SGA — as adesões sobem normalmente.',
    };
  }

  return {
    texto: 'sem código — não sobe ao SGA',
    variante: 'pendente',
    descricao:
      'Cadastre o modelo na tela do SGA e informe o código aqui. Sem ele, a adesão fecha no SAAC mas não sobe ao SGA.',
  };
}

/**
 * Marcas diacríticas separadas pelo `normalize('NFD')`.
 *
 * ⚠️ Montado com `new RegExp` a partir de string ASCII **de propósito**: o literal
 * `/[̀-ͯ]/` escrito com os caracteres reais vira combinante invisível ao
 * passar por ferramentas de edição, e aí o regex silenciosamente deixa de casar.
 */
const DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g');

/** Mesma normalização da busca do backend: sem acento, sem caixa. */
const normalizar = (texto: string) =>
  texto.normalize('NFD').replace(DIACRITICOS, '').trim().toLowerCase();

/**
 * Marcas que **casam a busca e ainda não têm nenhum modelo**.
 *
 * 🔴 A lista da tela mostra MODELOS, então buscar por uma marca recém-criada
 * devolve vazio — correto, e indistinguível de "a busca não funciona". Foi o
 * defeito que a at-45 corrigiu com um estado vazio explicativo, e a regra vem
 * para `utils/` porque a lista e o vazio precisam concordar sobre o que casou.
 */
export function marcasSemModeloQueCasam(marcas: BikeMarca[], busca: string): BikeMarca[] {
  if (!busca.trim()) return [];

  const alvo = normalizar(busca);

  return marcas.filter((m) => m.totalModelos === 0 && normalizar(m.nome).includes(alvo));
}

/** Ficha técnica resumida da linha ("250 W · 500 Wh · 60 km"). Vazia vira "—". */
export function fichaTecnicaResumida(bike: BikeEletrica): string {
  return (
    [
      bike.potenciaW ? `${bike.potenciaW} W` : null,
      bike.bateriaWh ? `${bike.bateriaWh} Wh` : null,
      bike.autonomiaKm ? `${bike.autonomiaKm} km` : null,
    ]
      .filter(Boolean)
      .join(' · ') || '—'
  );
}
